import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../components/api/axiosInstance";
import { portalTypeLabel } from "../utils/loginDomain";
import "../assets/css/LoginFixes.css";

function LoginFixes({ onBack }) {
  const [items, setItems] = useState([]);
  const [flags, setFlags] = useState({ enforce_login_domain: false, enforce_portal_host: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [drafts, setDrafts] = useState({});
  const [selected, setSelected] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/login-fixes");
      const data = res.data?.data || {};
      const rows = Array.isArray(data.items) ? data.items : [];
      setItems(rows);
      setFlags({
        enforce_login_domain: !!data.enforce_login_domain,
        enforce_portal_host: !!data.enforce_portal_host,
      });
      setDrafts(
        Object.fromEntries(
          rows.map((r) => [r.User_Id, r.Suggestion_Taken ? "" : r.Suggested_Email || ""])
        )
      );
      setSelected({});
      setRowErrors({});
    } catch (err) {
      setError(err.response?.data?.message || "Could not load login issues.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((r) =>
      [r.Name, r.Company_Email, r.Tenant_Name, r.Portal_Host]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, search]);

  const selectedIds = visible.filter((r) => selected[r.User_Id]).map((r) => r.User_Id);
  const allSelected = visible.length > 0 && selectedIds.length === visible.length;

  const apply = async (userIds) => {
    const fixes = userIds
      .map((id) => ({ User_Id: id, Company_Email: (drafts[id] || "").trim() }))
      .filter((f) => f.Company_Email);
    if (!fixes.length) {
      setMessage("Enter a new email for the selected logins first.");
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      const res = await axiosInstance.post("/login-fixes/apply", { fixes });
      const results = res.data?.data?.results || [];
      const failed = Object.fromEntries(
        results.filter((r) => !r.ok).map((r) => [r.User_Id, r.message])
      );
      const okIds = new Set(results.filter((r) => r.ok).map((r) => r.User_Id));
      setItems((prev) => prev.filter((r) => !okIds.has(r.User_Id)));
      setSelected((prev) => {
        const next = { ...prev };
        okIds.forEach((id) => delete next[id]);
        return next;
      });
      setRowErrors((prev) => {
        const next = { ...prev, ...failed };
        okIds.forEach((id) => delete next[id]);
        return next;
      });
      setMessage(res.data?.message || "Done.");
    } catch (err) {
      setMessage(err.response?.data?.message || "Could not apply fixes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="accounts-container">
      <div className="main-card loginfix">
        <div className="loginfix-header">
          <div>
            <h2 className="loginfix-title">Login email fixes</h2>
            <p className="loginfix-sub">
              Logins whose email breaks their company's portal rule. Staff must use @tumma.co,
              own-domain clients must use their domain, and shared-portal clients can't use @tumma.co.
            </p>
          </div>
          <div className="loginfix-actions">
            {onBack ? (
              <button type="button" className="btn-clear" onClick={onBack}>
                Back
              </button>
            ) : null}
            <button type="button" className="btn-clear" onClick={load} disabled={loading || saving}>
              Refresh
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => apply(selectedIds)}
              disabled={saving || selectedIds.length === 0}
            >
              {saving ? "Saving…" : `Apply selected (${selectedIds.length})`}
            </button>
          </div>
        </div>

        <div className="loginfix-flags">
          <span className={`loginfix-flag ${flags.enforce_login_domain ? "is-on" : ""}`}>
            Email rule: {flags.enforce_login_domain ? "enforced" : "warning only"}
          </span>
          <span className={`loginfix-flag ${flags.enforce_portal_host ? "is-on" : ""}`}>
            Portal host: {flags.enforce_portal_host ? "enforced" : "warning only"}
          </span>
        </div>

        <input
          type="text"
          className="free-search-input loginfix-search"
          placeholder="Search name, email, client or host"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {message ? <div className="loginfix-message">{message}</div> : null}
        {error ? <div className="loginfix-error">{error}</div> : null}

        {loading ? (
          <div className="loginfix-empty">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="loginfix-empty">
            {items.length === 0 ? "Every login matches its portal rule." : "No matches."}
          </div>
        ) : (
          <div className="loginfix-table-wrap">
            <table className="loginfix-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) =>
                        setSelected(
                          e.target.checked
                            ? Object.fromEntries(visible.map((r) => [r.User_Id, true]))
                            : {}
                        )
                      }
                      aria-label="Select all"
                    />
                  </th>
                  <th>Name</th>
                  <th>Current email</th>
                  <th>Company / portal</th>
                  <th>Issue</th>
                  <th>New email</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.User_Id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!selected[r.User_Id]}
                        onChange={(e) =>
                          setSelected((prev) => ({ ...prev, [r.User_Id]: e.target.checked }))
                        }
                        aria-label={`Select ${r.Name || r.Company_Email}`}
                      />
                    </td>
                    <td>{r.Name || "—"}</td>
                    <td className="loginfix-mono">{r.Company_Email}</td>
                    <td>
                      <div>{r.Tenant_Name || "—"}</div>
                      <div className="loginfix-muted">
                        {r.Is_Platform ? "Staff" : portalTypeLabel(r.Portal_Type)} · {r.Portal_Host}
                      </div>
                    </td>
                    <td className="loginfix-issue">{r.Issue}</td>
                    <td>
                      <input
                        type="email"
                        className="loginfix-input"
                        value={drafts[r.User_Id] || ""}
                        placeholder={r.Required_Domain ? `name@${r.Required_Domain}` : "new email"}
                        onChange={(e) =>
                          setDrafts((prev) => ({ ...prev, [r.User_Id]: e.target.value }))
                        }
                      />
                      {r.Suggestion_Taken ? (
                        <div className="loginfix-muted">
                          Suggested {r.Suggested_Email} is already taken.
                        </div>
                      ) : null}
                      {rowErrors[r.User_Id] ? (
                        <div className="loginfix-row-error">{rowErrors[r.User_Id]}</div>
                      ) : null}
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-primary loginfix-apply"
                        onClick={() => apply([r.User_Id])}
                        disabled={saving || !(drafts[r.User_Id] || "").trim()}
                      >
                        Apply
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginFixes;
